import * as React from 'react';

import classnames from 'classnames';

// @ts-expect-error
import {ColorName, colors} from '../colors/colors';

import styles from './card.module.css';

// ai-animated always has border
type CardVariant = 'default' | 'ai' | 'ai-animated';
export type CardProps = {
    children: React.ReactNode;
    hasBorder: boolean;
    /** By default, we will show a shadow if `hasBorder` is false, but not if it is true. */
    hasShadow?: boolean;
    /** If hasBorder is `true`, optionally change the color. */
    borderColor?: string;
    hoverBorderColor?: ColorName;
    /** Optionally change the border radius. */
    borderRadius?: number;
    /** If hasDottedBorder is `true`, display a dotted grey border. */
    hasDottedBorder?: boolean;
    /** If true, the card will size horizontally according to the content, not the parent's width */
    fitContent: boolean;
    /** If true, the card will size vertically according to the content, not the parent's height */
    fitHeight: boolean;
    /** We can optionally add a colored line along the left side */
    leftLineColor?: string;
    onClick?: (e: React.SyntheticEvent<any>) => void;
    getCardRef?: any;
    backgroundColor?: ColorName;
    hoverBackgroundColor?: ColorName;
    overflowVisible?: boolean;
    variant?: CardVariant;
    className?: string;
};

Card.defaultProps = {
    borderColor: colors.offWhiteDk,
    fitContent: false,
    fitHeight: false,
    variant: 'default',
};

Card.displayName = 'Card';

/**
 * This is a simple UI component that gives us a standard way of creating
 * a "card" with rounded corners and an optional shadow.
 *
 * It is up to the component using the Card shell to provide all of the
 * contents via \`children\`.
 */
export function Card({
    hasBorder,
    hasShadow,
    children,
    borderColor,
    hoverBorderColor,
    borderRadius,
    hasDottedBorder,
    fitContent,
    fitHeight,
    leftLineColor,
    onClick,
    getCardRef,
    backgroundColor,
    hoverBackgroundColor,
    overflowVisible,
    variant,
    className,
}: CardProps) {
    const [isHovered, setIsHovered] = React.useState(false);

    const containerStyleName = classnames(
        {
            [styles.card]: true,
            [styles['card--bordered']]: hasBorder,
            [styles['card--shadowed']]: typeof hasShadow !== 'undefined' ? hasShadow : !hasBorder,
            [styles['fit-content']]: fitContent,
            [styles['fit-height']]: fitHeight,
            [styles['card--button']]: onClick,
            [styles['overflow-visible']]: overflowVisible,
            [styles['card--variant-ai']]: variant === 'ai',
            [styles['card--variant-ai-bordered']]: variant === 'ai' && hasBorder,
            [styles['card--variant-ai-shadowed']]:
                (variant === 'ai' || variant === 'ai-animated') && hasShadow,
            [styles['card--variant-ai-animated']]: variant === 'ai-animated',
        },
        className
    );

    const getStyles = (): Record<any, any> => {
        const localStyles: Record<string, any> = {};

        if (hasDottedBorder) {
            localStyles.borderStyle = 'dashed';
            localStyles.borderColor = colors.grey;
        } else if (leftLineColor) {
            localStyles.borderColor = borderColor;
            localStyles.borderLeft = `3px solid ${leftLineColor}`;
        } else {
            localStyles.borderColor = borderColor;
            // have to reassign borderLeft if it was overwritten by leftLineColor
            localStyles.borderLeft = hasBorder && borderColor && `1px solid ${borderColor}`;
        }

        if (backgroundColor) {
            localStyles.backgroundColor = colors[backgroundColor];
        }

        if ((onClick || hoverBackgroundColor) && isHovered) {
            localStyles.backgroundColor = hoverBackgroundColor
                ? colors[hoverBackgroundColor]
                : colors.blueXlt;
        }

        if ((onClick || hoverBorderColor) && isHovered) {
            localStyles.borderColor = hoverBorderColor ? colors[hoverBorderColor] : colors.blue;
        }

        if (typeof borderRadius === 'number') {
            localStyles.borderRadius = `${borderRadius}px`;
        }

        return localStyles;
    };

    const cardElement = (
        <div
            ref={getCardRef}
            style={getStyles()}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={containerStyleName}
        >
            {children}
        </div>
    );

    if (variant === 'ai-animated') {
        return (
            <div
                style={{
                    borderRadius: borderRadius ? `${borderRadius}px` : undefined,
                    // @ts-expect-error
                    '--pseudo-border-radius': `${(borderRadius || 3) + 2}px`,
                }}
                className={styles['card-container--ai-animated']}
            >
                {cardElement}
            </div>
        );
    }

    return cardElement;
}
